import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { Link as RRLink } from 'react-router-dom';
import chroma from 'chroma-js';
import {
  TYPOGRAPHY_TRANSFORM,
  TYPOGRAPHY_WEIGHT,
  TYPOGRAPHY_FAMILY,
  TYPOGRAPHY_ALIGN,
} from '../types';
import {
  fontFamilyMap,
  sizesMap,
  weightMap,
  textLetterSpacing,
  textLineHeight,
  typographyMarginBottom,
} from '../utils';
import {
  SIZE,
  ActionType,
  LinkInternalType,
  LinkExternalType,
} from '../../types'; 
import { IconWrapper } from '../../icons';


type MARGIN = 'none';
type DISPLAY = 'block' | 'inline-block';
type OPACITY = 0 | 25 | 50 | 75 | 100;

type ActionTextStyledProps = {
  size: SIZE;
  weight: TYPOGRAPHY_WEIGHT;
  textTransform: TYPOGRAPHY_TRANSFORM;
  textOpacity: number;
  textDisplay: string;
  familyType: TYPOGRAPHY_FAMILY;
  align: TYPOGRAPHY_ALIGN;
  htmlFor?: string;
  margin?: MARGIN;
  color?: string;
  disabled?: boolean;
};

const ActionTextStyled = styled.span<ActionTextStyledProps>`
  display: ${(p) => p.textDisplay};
  font-family: ${(p) => p.theme.fonts[fontFamilyMap[p.familyType]]};
  font-weight: ${(p) => weightMap[p.weight]};
  font-size: ${(p) => sizesMap[p.size].size};
  line-height: ${textLineHeight};
  letter-spacing: ${textLetterSpacing};
  text-transform: ${(p) => p.textTransform};
  text-align: ${(p) => p.align};
  color: ${(p) =>
    p.disabled ? p.theme.colors.black : p.color ?? p.theme.colors.primary20b};
  padding: 0;
  opacity: ${(p) => p.textOpacity / 100};
  margin: ${(p) => typographyMarginBottom(p.margin === 'none')};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  text-decoration: none;
  align-items: center;

  & a {
    text-align: ${(p) => p.align};
    text-decoration: none;
    color: ${(p) => p.color ?? p.theme.colors.primary20b};
  }

  svg g {
    stroke: ${(p) =>
      p.disabled ? p.theme.colors.black : p.color ?? p.theme.colors.primary20b};
  }

  &:focus a,
  & a:focus,
  &:focus {
    outline: none;
    text-decoration: underline;
  }

  &:active a,
  &:hover a,
  & a:active,
  & a:hover,
  &:active,
  &:hover {
    color: ${(p) =>
      p.disabled
        ? p.theme.colors.black
        : p.color
        ? chroma(p.color).darken(0.4).hex()
        : p.theme.colors.primary40b};

    svg g {
      stroke: ${(p) =>
        p.disabled
          ? p.theme.colors.black
          : p.color
          ? chroma(p.color).darken(0.4).hex()
          : p.theme.colors.primary40b};
    }
  }
`;

type ActionTextBaseProps = {
  size?: SIZE;
  weight?: TYPOGRAPHY_WEIGHT;
  transform?: TYPOGRAPHY_TRANSFORM;
  margin?: MARGIN;
  display?: DISPLAY;
  familyType?: TYPOGRAPHY_FAMILY;
  opacity?: OPACITY;
  align?: TYPOGRAPHY_ALIGN;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  testid?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  'aria-label'?: string;
  children: React.ReactNode;
};

type ActionTextProps = ActionTextBaseProps &
  (ActionType | LinkInternalType | LinkExternalType);

function ActionText(props: ActionTextProps) {
  const {
    size = 'base',
    weight = 'normal',
    transform = 'uppercase',
    familyType = 'sans',
    opacity = 100,
    margin,
    display = 'inline-flex',
    align = 'left',
    iconLeft,
    iconRight,
    disabled,
    testid,
    children,
    style,
    'aria-label': ariaLabel,
  } = props;

  const color = style && 'color' in style ? (style.color as string) : undefined;
  const theme = useTheme();

  if (disabled) {
    return (
      <ActionTextStyled
        aria-label={ariaLabel}
        data-testid={testid}
        size={size}
        weight={weight}
        textTransform={transform}
        familyType={familyType}
        textOpacity={50}
        margin={margin}
        align={align}
        textDisplay={display}
        disabled={disabled}
        color={color}
        style={style}
      >
        {iconLeft && <IconWrapper
          hoverColor={theme.colors.transparent}>
        {iconLeft}</IconWrapper>}
        {children}
        {iconRight && <IconWrapper
          hoverColor={theme.colors.transparent}>
        {iconRight}</IconWrapper>}
      </ActionTextStyled>
    );
  }

  if (props.type === 'link-internal' || props.type === 'link') {
    const Link = props.linkComponent ?? RRLink;

    return (
      <ActionTextStyled
        aria-label={ariaLabel}
        data-testid={testid}
        size={size}
        weight={weight}
        textTransform={transform}
        familyType={familyType}
        textOpacity={opacity}
        margin={margin}
        align={align}
        textDisplay={display}
        color={color}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {iconLeft && <IconWrapper
          hoverColor={theme.colors.transparent}>
            {iconLeft}</IconWrapper>}
        <Link to={props.to}>{children}</Link>
        {iconRight && <IconWrapper
          hoverColor={theme.colors.transparent}>
          {iconRight}</IconWrapper>}
      </ActionTextStyled>
    );
  }

  if (props.type === 'link-external') {
    return (
      <ActionTextStyled
        aria-label={ariaLabel}
        as="a"
        target={props.target ?? ''}
        rel={props.target === '_blank' ? 'noopener noreferrer' : ''}
        href={props.to}
        data-testid={testid}
        size={size}
        weight={weight}
        textTransform={transform}
        familyType={familyType}
        textOpacity={opacity}
        margin={margin}
        align={align}
        textDisplay={display}
        color={color}
        style={style}
      >
        {iconLeft && <IconWrapper
          hoverColor={theme.colors.transparent}>
        {iconLeft}</IconWrapper>}
        {children}
        {iconRight && <IconWrapper
          hoverColor={theme.colors.transparent}>
          {iconRight}</IconWrapper>}
      </ActionTextStyled>
    );
  }

  if (props.type === 'action') {
    return (
      <ActionTextStyled
        aria-label={ariaLabel}
        tabIndex={0}
        onClick={props.onClick}
        data-testid={testid}
        size={size}
        weight={weight}
        textTransform={transform}
        familyType={familyType}
        textOpacity={opacity}
        margin={margin}
        align={align}
        textDisplay={display}
        color={color}
        style={style}
      >
        {iconLeft && <IconWrapper
          hoverColor={theme.colors.transparent}>
        {iconLeft}</IconWrapper>}
        {children}
        {iconRight && <IconWrapper
          hoverColor={theme.colors.transparent}>
        {iconRight}</IconWrapper>}
      </ActionTextStyled>
    );
  }

  console.warn('There is no valid type for ActionText!');
  return null;
}

export default React.memo(ActionText);
